import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  FileText,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react'
import { useDirty } from '@/hooks/useDirty'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Combobox, type ComboboxOption } from '@/components/Combobox'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatCurrencyPrecise, formatDate } from '@/lib/locale'
import { coaApi } from '@/features/coa/api'
import { useCoa } from '@/features/coa/queries'
import { journalEntryApi } from '@/features/journalEntry/api'
import {
  useDeleteJournalEntry,
  useJournalEntries,
  useJournalEntry,
  useSaveJournalEntry,
} from '@/features/journalEntry/queries'
import { usePostingStatus, usePostSelected } from '@/features/accruals/queries'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import type { JeLine, JePayload, JeStatus, JournalEntry } from '@/features/journalEntry/types'

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function todayIso(): string {
  const d = new Date()
  return d.toISOString().slice(0, 10)
}

function monthEndLabel(isoDate: string): string {
  if (!isoDate) return ''
  const d = new Date(isoDate + 'T00:00:00')
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return formatDate(last, { year: 'numeric', month: 'long', day: 'numeric' })
}

function fmtMoney(n: number | string | null | undefined): string {
  const v = typeof n === 'number' ? n : parseFloat(String(n ?? 0))
  return formatCurrencyPrecise(isFinite(v) ? v : 0)
}

function statusBadgeVariant(status: JeStatus): 'secondary' | 'success' | 'danger' {
  if (status === 'posted') return 'success'
  if (status === 'voided') return 'danger'
  return 'secondary'
}

type View =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'edit'; id: number }

export function JournalEntryPage() {
  const [view, setView] = useState<View>({ kind: 'list' })
  // Why: clicking the "Journal Entries" sidebar link while already inside
  // the editor used to leave the inner view stuck on 'new'/'edit' because
  // the URL didn't change. React Router still bumps location.key on a
  // same-path navigation, so resetting view to 'list' on every key change
  // makes the sidebar (and browser back/forward) behave like the user
  // expects - they're always taken back to the list.
  const location = useLocation()
  useEffect(() => {
    setView({ kind: 'list' })
  }, [location.key])

  return (
    <Page
      title="Journal Entries"
      description="Draft, review, save, and delete manual journal entries. Posting to the general ledger is done from the list view via the Post Now banner."
      width="wide"
    >
      {view.kind === 'list' ? (
        <EntriesList
          onNew={() => setView({ kind: 'new' })}
          onOpen={(id) => setView({ kind: 'edit', id })}
        />
      ) : (
        <EntryForm
          key={view.kind === 'edit' ? `edit-${view.id}` : 'new'}
          currentId={view.kind === 'edit' ? view.id : null}
          onBack={() => setView({ kind: 'list' })}
        />
      )}
    </Page>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// List view
// ──────────────────────────────────────────────────────────────────────────

function EntriesList({
  onNew,
  onOpen,
}: {
  onNew: () => void
  onOpen: (id: number) => void
}) {
  const q = useJournalEntries()
  const posting = usePostingStatus()
  const postSelectedMut = usePostSelected()
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)
  const { toast } = useToast()

  const unpostedJe = posting.data?.unpostedJournalEntries ?? 0

  const jeItems = useMemo<PostingSelectorItem[]>(() => {
    const rows = unpostedPreview.data?.journalEntries ?? []
    return rows.map((r) => ({
      id: r.id,
      cells: [
        r.entryDate ? formatDate(r.entryDate) : '-',
        <span className="font-mono text-xs">{r.entryNumber || `JE #${r.id}`}</span>,
        <span className="block max-w-[420px] truncate" title={r.description ?? undefined}>
          {r.description || '-'}
        </span>,
        fmtMoney(r.totalAmount),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
    }))
  }, [unpostedPreview.data])

  const jeColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Entry #', width: 'w-32' },
    { header: 'Description' },
    { header: 'Total', align: 'right' as const, width: 'w-28' },
  ]

  const runPost = async (selection: Record<string, number[]>) => {
    const ids = selection.journal ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'journal', ids })
      toast({
        title: 'Posted to GL',
        description: `Posted ${r.posted ?? 0} accounting entr${(r.posted ?? 0) === 1 ? 'y' : 'ies'} from ${ids.length} journal entry/entries.`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: errorMessage(err),
      })
    }
  }

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load journal entries: {errorMessage(q.error)}</AlertDescription>
      </Alert>
    )
  }

  const entries = q.data ?? []

  // Why: posting-banner + selector modal share the same lifecycle as
  // the list, so they're co-located here. Empty-state branch below
  // also shows the banner because in theory a draft from another user
  // could be present even if this list is empty (it isn't, with the
  // current schema - but the banner is harmless in that case).
  const banner = (
    <>
      <PostingBanner
        count={unpostedJe}
        label="journal entries"
        singularLabel="journal entry"
        source="journal"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending journal entries' : 'Post journal entries'}
        description={selectorReadOnly
          ? 'These draft journal entries are queued to post. Click Post Now to choose which to post.'
          : 'Tick the entries you want to post. Posting only affects the rows you select - bank statements, accruals, and schedules are untouched.'}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        tabs={[
          {
            key: 'journal',
            label: 'Journal entries',
            items: jeItems,
            columns: jeColumns,
            emptyMessage: 'No draft journal entries are pending.',
          },
        ]}
        onPost={runPost}
      />
    </>
  )

  if (entries.length === 0) {
    return (
      <div className="space-y-4">
        {banner}
        <Card>
          <CardContent className="py-6">
            <EmptyState
              icon={FileText}
              title="No journal entries yet"
              description="Create your first journal entry to start posting adjustments to the general ledger."
              action={
                <Button onClick={onNew}>
                  <Plus className="size-4" /> New entry
                </Button>
              }
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {banner}
      <Card>
        <CardContent className="space-y-4 pt-6">
          <Toolbar
            align="between"
            leading={<h2 className="text-base font-semibold text-foreground">All entries</h2>}
          >
            <Button onClick={onNew}>
              <Plus className="size-4" /> New entry
            </Button>
          </Toolbar>

        <DataTable
          rows={entries}
          onRowClick={(r) => onOpen(r.id)}
          sortable={false}
          columns={[
            {
              header: 'Entry #',
              accessorKey: 'entry_number',
              cell: ({ row }) => <span className="font-semibold">{row.original.entry_number}</span>,
            },
            {
              header: 'Date',
              accessorKey: 'entry_date',
              cell: ({ row }) =>
                row.original.entry_date ? new Date(row.original.entry_date).toLocaleDateString() : '-',
            },
            {
              header: 'Description',
              accessorKey: 'description',
              cell: ({ row }) => (
                <span className="block max-w-[280px] truncate">{row.original.description || '-'}</span>
              ),
            },
            {
              header: () => <span className="block text-right">Debit</span>,
              accessorKey: 'total_debit',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs">
                  {fmtMoney(row.original.total_debit)}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Credit</span>,
              accessorKey: 'total_credit',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs">
                  {fmtMoney(row.original.total_credit)}
                </span>
              ),
            },
            {
              header: 'Status',
              accessorKey: 'status',
              cell: ({ row }) => (
                <Badge variant={statusBadgeVariant(row.original.status)}>
                  {row.original.status.charAt(0).toUpperCase() + row.original.status.slice(1)}
                </Badge>
              ),
            },
          ]}
        />
        </CardContent>
      </Card>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Form view
// ──────────────────────────────────────────────────────────────────────────

type RowDraft = {
  uid: string
  line_date: string
  description: string
  account_code: string
  debit: string
  credit: string
}

let rowSeq = 0
const nextRowUid = () => `row-${++rowSeq}`

function emptyRow(defaultDate: string): RowDraft {
  return {
    uid: nextRowUid(),
    line_date: defaultDate,
    description: '',
    account_code: '',
    debit: '',
    credit: '',
  }
}

function rowFromLine(line: JeLine): RowDraft {
  // pg returns NUMERIC columns as strings; the type lies. Coerce before
  // formatting so .toFixed doesn't throw and crash the form.
  const debit = Number(line.debit) || 0
  const credit = Number(line.credit) || 0
  return {
    uid: nextRowUid(),
    line_date: line.line_date ? line.line_date.slice(0, 10) : '',
    description: line.description ?? '',
    account_code: line.account_code ?? '',
    debit: debit > 0 ? debit.toFixed(2) : '',
    credit: credit > 0 ? credit.toFixed(2) : '',
  }
}

type FormState = {
  entry_number: string
  entry_date: string
  date_created_display: string
  created_by: string
  description: string
  recurring: boolean
  frequency: string
  start_date: string
  end_date: string
  lines: RowDraft[]
}

function makeDefaultState(): FormState {
  const today = todayIso()
  return {
    entry_number: 'JE-1',
    entry_date: today,
    date_created_display: new Date().toLocaleString(),
    created_by: '',
    description: '',
    recurring: false,
    frequency: '',
    start_date: '',
    end_date: '',
    lines: [emptyRow(today), emptyRow(today)],
  }
}

function stateFromEntry(entry: JournalEntry): FormState {
  return {
    entry_number: entry.entry_number,
    entry_date: entry.entry_date ? entry.entry_date.slice(0, 10) : '',
    date_created_display: entry.date_created
      ? new Date(entry.date_created).toLocaleString()
      : '',
    created_by: entry.created_by ?? '',
    description: entry.description ?? '',
    recurring: !!entry.recurring,
    frequency: entry.frequency ?? '',
    start_date: entry.start_date ? entry.start_date.slice(0, 10) : '',
    end_date: entry.end_date ? entry.end_date.slice(0, 10) : '',
    lines: (entry.lines ?? []).map(rowFromLine),
  }
}

function EntryForm({
  currentId,
  onBack,
}: {
  currentId: number | null
  onBack: () => void
}) {
  const { toast } = useToast()
  const existing = useJournalEntry(currentId)
  const coa = useCoa()
  const saveMut = useSaveJournalEntry()
  const deleteMut = useDeleteJournalEntry()

  const [form, setForm] = useState<FormState>(() => makeDefaultState())
  const [savedId, setSavedId] = useState<number | null>(currentId)
  const [status, setStatus] = useState<JeStatus>('draft')
  const [alert, setAlert] = useState<{ title: string; body: React.ReactNode } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Bootstrap "new entry" - fetch next number + created-by name
  useEffect(() => {
    let cancelled = false
    if (currentId !== null) return
    void (async () => {
      try {
        const [numberResp, profile] = await Promise.all([
          journalEntryApi.nextNumber().catch(() => ({ entryNumber: 'JE-1' })),
          coaApi.profile().catch(() => ({}) as { name?: string; email?: string }),
        ])
        if (cancelled) return
        setForm((prev) => ({
          ...prev,
          entry_number: numberResp.entryNumber || 'JE-1',
          created_by: profile.name ?? profile.email ?? '',
        }))
      } catch {
        /* ignore - defaults remain */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [currentId])

  // Hydrate form when an existing entry arrives
  useEffect(() => {
    if (currentId !== null && existing.data) {
      setForm(stateFromEntry(existing.data))
      setStatus(existing.data.status)
      setSavedId(existing.data.id)
    }
  }, [currentId, existing.data])

  const readOnly = status === 'posted' || status === 'voided'

  const { mainOptions, subOptions } = useMemo(() => {
    const taxonomy = coa.data ?? []
    const mainByCode = new Map<string, ComboboxOption<string>>()
    const subByCode = new Map<string, ComboboxOption<string>>()
    for (const item of taxonomy) {
      if (!mainByCode.has(item.account_code)) {
        mainByCode.set(item.account_code, {
          value: item.account_code,
          label: `${item.account_code} - ${item.main_category}`,
          description: item.statement_type,
        })
      }
      if (!subByCode.has(item.account_code)) {
        subByCode.set(item.account_code, {
          value: item.account_code,
          label: `${item.account_code} - ${item.subcategory_name}`,
          description: `${item.main_category} · ${item.statement_type}`,
        })
      }
    }
    return {
      mainOptions: Array.from(mainByCode.values()),
      subOptions: Array.from(subByCode.values()),
    }
  }, [coa.data])

  const totals = useMemo(() => {
    let td = 0
    let tc = 0
    for (const r of form.lines) {
      td += parseFloat(r.debit) || 0
      tc += parseFloat(r.credit) || 0
    }
    const balance = Math.round((td - tc) * 100) / 100
    return { totalDebit: td, totalCredit: tc, balance }
  }, [form.lines])

  const isBalanced = Math.abs(totals.balance) < 0.005

  const updateLine = (uid: string, patch: Partial<RowDraft>) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((r) => (r.uid === uid ? { ...r, ...patch } : r)),
    }))
  }

  const onDebitInput = (uid: string, value: string) => {
    const clearsCredit = parseFloat(value) > 0
    updateLine(uid, { debit: value, ...(clearsCredit ? { credit: '' } : {}) })
  }
  const onCreditInput = (uid: string, value: string) => {
    const clearsDebit = parseFloat(value) > 0
    updateLine(uid, { credit: value, ...(clearsDebit ? { debit: '' } : {}) })
  }

  const addRow = () => {
    setForm((prev) => ({
      ...prev,
      lines: [...prev.lines, emptyRow(prev.entry_date)],
    }))
  }

  const removeRow = (uid: string) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((r) => r.uid !== uid),
    }))
  }

  const validate = (): string | null => {
    if (!form.entry_date) return 'Date Recorded is required.'
    if (form.lines.length < 2) return 'At least 2 line items are required.'
    for (let i = 0; i < form.lines.length; i++) {
      if (!form.lines[i].account_code) return `Line ${i + 1}: Account is required.`
    }
    return null
  }

  const collectPayload = (): JePayload => ({
    entry_number: form.entry_number,
    entry_date: form.entry_date,
    description: form.description || null,
    recurring: form.recurring,
    frequency: form.recurring ? form.frequency || null : null,
    start_date: form.recurring ? form.start_date || null : null,
    end_date: form.recurring ? form.end_date || null : null,
    lines: form.lines.map((r) => ({
      line_date: r.line_date,
      description: r.description,
      account_code: r.account_code,
      debit: parseFloat(r.debit) || 0,
      credit: parseFloat(r.credit) || 0,
    })),
  })

  const handleSave = async () => {
    const err = validate()
    if (err) {
      setAlert({ title: 'Validation error', body: err })
      return
    }
    try {
      const result = await saveMut.mutateAsync({ id: savedId, body: collectPayload() })
      if (result.id) setSavedId(result.id)
      toast({ title: 'Journal entry saved.' })
      // Why: after a successful save, drop back to the main list - the
      // user has finished with this entry. Posting (if needed) is done
      // from the list's "Post Now" banner, which is exactly where they
      // land. Avoids leaving the editor open with stale dirty state.
      onBack()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: errorMessage(e),
      })
    }
  }

  const handleDelete = () => {
    if (savedId == null) return
    setConfirmDelete(true)
  }

  const runDelete = async () => {
    if (savedId == null) return
    try {
      await deleteMut.mutateAsync(savedId)
      toast({
        title: status === 'posted' ? 'Entry voided.' : 'Entry deleted.',
      })
      setConfirmDelete(false)
      onBack()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: errorMessage(e),
      })
    }
  }

  // Dirty-check the editor. Baseline is the saved entry's snapshot for
  // edits and the empty-state default (with bootstrap entry_number /
  // created_by) for new entries. The auto-fetched entry_number and
  // created_by are intentionally treated as part of the baseline so
  // they don't immediately flip Save on for a brand-new entry. Declared
  // BEFORE the loading/error early returns so hook order stays stable.
  const baselineForm = useMemo(() => {
    if (currentId !== null && existing.data) {
      return stateFromEntry(existing.data)
    }
    return {
      ...makeDefaultState(),
      entry_number: form.entry_number,
      created_by: form.created_by,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, existing.data, form.entry_number, form.created_by])
  const dirty = useDirty(form, baselineForm)

  if (currentId !== null && existing.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  if (currentId !== null && existing.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Could not load journal entry: {errorMessage(existing.error)}
        </AlertDescription>
      </Alert>
    )
  }

  const busy = saveMut.isPending || deleteMut.isPending

  return (
    <div className="space-y-6">
      {/* Why: prominent Back button at the very top so the user always has
          an obvious way back to the list, even if they don't notice the
          one in the action toolbar further down the page. */}
      <div>
        <Button variant="ghost" onClick={onBack} disabled={busy}>
          <ArrowLeft className="size-4" /> Back to journal entries
        </Button>
      </div>

      {/* Header card */}
      <Card>
        <CardContent className="space-y-4 pt-6">
          <Toolbar
            align="between"
            leading={
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-foreground">
                  Journal Entry for the month ending:{' '}
                  <span className="text-muted-foreground">{monthEndLabel(form.entry_date)}</span>
                </h2>
              </div>
            }
          >
            <Badge variant={statusBadgeVariant(status)}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
          </Toolbar>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <FormField
              label="Entry #"
              id="je-number"
              value={form.entry_number}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, entry_number: e.currentTarget.value })}
            />
            <FormField
              label="Date recorded"
              id="je-date"
              type="date"
              value={form.entry_date}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, entry_date: e.currentTarget.value })}
            />
            <FormField
              label="Date created"
              id="je-date-created"
              value={form.date_created_display || '-'}
              readOnly
              disabled
            />
            <FormField
              label="Created by"
              id="je-created-by"
              value={form.created_by || '-'}
              readOnly
              disabled
            />
            <div className="md:col-span-2 lg:col-span-3">
              <FormField
                label="Description"
                id="je-description"
                control={
                  <Textarea
                    id="je-description"
                    rows={5}
                    placeholder="Internal note…"
                    disabled={readOnly}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
                    className="min-h-[120px] resize-y"
                  />
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                id="je-recurring"
                disabled={readOnly}
                checked={form.recurring}
                onCheckedChange={(v) => setForm({ ...form, recurring: v === true })}
              />
              Recurring
            </label>
          </div>

          {form.recurring ? (
            <div className="grid grid-cols-1 gap-3 border-t pt-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <label htmlFor="je-frequency" className="text-sm font-medium">
                  Frequency
                </label>
                <Select
                  value={form.frequency || undefined}
                  onValueChange={(v) => setForm({ ...form, frequency: v })}
                  disabled={readOnly}
                >
                  <SelectTrigger id="je-frequency">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormField
                label="Start date"
                id="je-start-date"
                type="date"
                disabled={readOnly}
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.currentTarget.value })}
              />
              <FormField
                label="End date"
                id="je-end-date"
                type="date"
                disabled={readOnly}
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.currentTarget.value })}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Toolbar */}
      <Toolbar align="between">
        {!readOnly ? (
          <>
            <Button variant="primary" onClick={handleSave} disabled={!dirty || busy}>
              {saveMut.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Save
            </Button>
            {savedId != null ? (
              <Button variant="danger" onClick={handleDelete} disabled={busy}>
                <Trash2 className="size-4" />
                {status === 'posted' ? 'Void' : 'Delete'}
              </Button>
            ) : null}
            <Button variant="outline" onClick={addRow} disabled={busy}>
              <Plus className="size-4" /> Add row
            </Button>
          </>
        ) : null}
      </Toolbar>

      {/* Line items - CSS grid, horizontal-scroll on narrow viewports; stacked card layout on xs. */}
      <Card>
        <CardContent className="p-0">
          {/* md+ : grid layout with horizontal scroll if needed */}
          <div className="hidden overflow-x-auto md:block">
            <div className="min-w-[920px]">
              <LineHeader />
              {form.lines.map((row, idx) => (
                <LineGridRow
                  key={row.uid}
                  row={row}
                  idx={idx}
                  mainOptions={mainOptions}
                  subOptions={subOptions}
                  readOnly={readOnly}
                  comboboxDisabled={readOnly || coa.isLoading}
                  onUpdate={(patch) => updateLine(row.uid, patch)}
                  onRemove={() => removeRow(row.uid)}
                  onDebit={(v) => onDebitInput(row.uid, v)}
                  onCredit={(v) => onCreditInput(row.uid, v)}
                />
              ))}
              <LineTotalsRow totals={totals} isBalanced={isBalanced} />
            </div>
          </div>

          {/* xs/sm: stacked cards (each line is a mini-card with labels) */}
          <div className="space-y-3 p-4 md:hidden">
            {form.lines.map((row, idx) => (
              <LineMobileCard
                key={row.uid}
                row={row}
                idx={idx}
                mainOptions={mainOptions}
                subOptions={subOptions}
                readOnly={readOnly}
                comboboxDisabled={readOnly || coa.isLoading}
                onUpdate={(patch) => updateLine(row.uid, patch)}
                onRemove={() => removeRow(row.uid)}
                onDebit={(v) => onDebitInput(row.uid, v)}
                onCredit={(v) => onCreditInput(row.uid, v)}
              />
            ))}
            <div className="rounded-md border bg-muted/50 p-3 text-sm">
              <div className="flex justify-between">
                <span>Total debit</span>
                <span className="font-mono font-semibold">{fmtMoney(totals.totalDebit)}</span>
              </div>
              <div className="flex justify-between">
                <span>Total credit</span>
                <span className="font-mono font-semibold">{fmtMoney(totals.totalCredit)}</span>
              </div>
              <div
                className={cn(
                  'mt-1 flex justify-between border-t pt-1 text-sm font-semibold',
                  isBalanced ? 'text-success' : 'text-destructive',
                )}
              >
                <span>{isBalanced ? 'Balanced' : 'Out of balance'}</span>
                <span className="font-mono">
                  {isBalanced ? fmtMoney(0) : fmtMoney(Math.abs(totals.balance))}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal.Alert
        open={alert !== null}
        onOpenChange={(open) => !open && setAlert(null)}
        title={alert?.title ?? ''}
        body={alert?.body}
        onClose={() => setAlert(null)}
      />

      <Modal.Confirm
        open={confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(false)}
        title={status === 'posted' ? 'Void posted entry?' : 'Delete draft entry?'}
        body={
          status === 'posted' ? (
            <p>
              This entry is posted. Voiding it will create reversing entries in the General Ledger.
              Continue?
            </p>
          ) : (
            <p>Delete this draft journal entry? This cannot be undone.</p>
          )
        }
        confirmLabel={status === 'posted' ? 'Void entry' : 'Delete entry'}
        onConfirm={runDelete}
        onCancel={() => setConfirmDelete(false)}
        busy={deleteMut.isPending}
      />
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Line item row helpers - keep the layout logic in one place so md+ grid
// and xs card stay visually consistent and easy to update together.
// ──────────────────────────────────────────────────────────────────────────

const LINE_GRID =
  'grid items-center gap-2 grid-cols-[48px_140px_1fr_240px_130px_130px_240px_56px]'

function LineHeader() {
  return (
    <div
      className={cn(
        LINE_GRID,
        'border-b bg-primary-gradient px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground',
      )}
    >
      <div className="text-center">#</div>
      <div>Date</div>
      <div>Description</div>
      <div>Account #</div>
      <div className="text-right">Debit</div>
      <div className="text-right">Credit</div>
      <div>Sub-category</div>
      <div />
    </div>
  )
}

interface LineRowProps {
  row: RowDraft
  idx: number
  mainOptions: ComboboxOption<string>[]
  subOptions: ComboboxOption<string>[]
  readOnly: boolean
  comboboxDisabled: boolean
  onUpdate: (patch: Partial<RowDraft>) => void
  onRemove: () => void
  onDebit: (v: string) => void
  onCredit: (v: string) => void
}

function LineGridRow(p: LineRowProps) {
  return (
    <div className={cn(LINE_GRID, 'border-b px-3 py-2 last:border-b-0')}>
      <div className="text-center text-xs font-medium text-muted-foreground">{p.idx + 1}</div>
      <Input
        type="date"
        value={p.row.line_date}
        disabled={p.readOnly}
        onChange={(e) => p.onUpdate({ line_date: e.currentTarget.value })}
        className="h-9"
      />
      <Input
        placeholder="Line description…"
        value={p.row.description}
        disabled={p.readOnly}
        onChange={(e) => p.onUpdate({ description: e.currentTarget.value })}
        className="h-9"
      />
      <Combobox
        options={p.mainOptions}
        value={p.row.account_code || null}
        disabled={p.comboboxDisabled}
        placeholder="Pick account…"
        onChange={(v) => p.onUpdate({ account_code: v ?? '' })}
        maxResults={10000}
        popoverClassName="w-[360px] max-w-[90vw]"
      />
      <Input
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        placeholder="0.00"
        value={p.row.debit}
        disabled={p.readOnly}
        onChange={(e) => p.onDebit(e.currentTarget.value)}
        className="h-9 text-right font-mono"
      />
      <Input
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        placeholder="0.00"
        value={p.row.credit}
        disabled={p.readOnly}
        onChange={(e) => p.onCredit(e.currentTarget.value)}
        className="h-9 text-right font-mono"
      />
      <Combobox
        options={p.subOptions}
        value={p.row.account_code || null}
        disabled={p.comboboxDisabled}
        placeholder="Pick sub-category…"
        onChange={(v) => p.onUpdate({ account_code: v ?? '' })}
        maxResults={10000}
        popoverClassName="w-[360px] max-w-[90vw]"
      />
      <div className="flex justify-center">
        {!p.readOnly ? (
          <Button variant="ghost" size="icon" onClick={p.onRemove} title="Remove line">
            <Trash2 className="size-4 text-destructive" />
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function LineTotalsRow({
  totals,
  isBalanced,
}: {
  totals: { totalDebit: number; totalCredit: number; balance: number }
  isBalanced: boolean
}) {
  return (
    <div className={cn(LINE_GRID, 'border-t-2 bg-muted/50 px-3 py-2 text-sm font-semibold')}>
      <div className="col-span-4 text-right">Total</div>
      <div className="text-right font-mono">{fmtMoney(totals.totalDebit)}</div>
      <div className="text-right font-mono">{fmtMoney(totals.totalCredit)}</div>
      <div
        className={cn(
          'text-right font-mono',
          isBalanced ? 'text-success' : 'text-destructive',
        )}
      >
        {isBalanced ? fmtMoney(0) : `${fmtMoney(Math.abs(totals.balance))} - Out of balance`}
      </div>
      <div />
    </div>
  )
}

function LineMobileCard(p: LineRowProps) {
  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Line {p.idx + 1}
        </span>
        {!p.readOnly ? (
          <Button variant="ghost" size="icon" onClick={p.onRemove} title="Remove line">
            <Trash2 className="size-4 text-destructive" />
          </Button>
        ) : null}
      </div>
      <FormField
        label="Date"
        type="date"
        value={p.row.line_date}
        disabled={p.readOnly}
        onChange={(e) => p.onUpdate({ line_date: e.currentTarget.value })}
      />
      <FormField
        label="Description"
        placeholder="Line description…"
        value={p.row.description}
        disabled={p.readOnly}
        onChange={(e) => p.onUpdate({ description: e.currentTarget.value })}
      />
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Account #</label>
        <Combobox
          options={p.mainOptions}
          value={p.row.account_code || null}
          disabled={p.comboboxDisabled}
          placeholder="Pick account…"
          onChange={(v) => p.onUpdate({ account_code: v ?? '' })}
          maxResults={10000}
          popoverClassName="w-[360px] max-w-[90vw]"
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Sub-category</label>
        <Combobox
          options={p.subOptions}
          value={p.row.account_code || null}
          disabled={p.comboboxDisabled}
          placeholder="Pick sub-category…"
          onChange={(v) => p.onUpdate({ account_code: v ?? '' })}
          maxResults={10000}
          popoverClassName="w-[360px] max-w-[90vw]"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FormField
          label="Debit"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="0.00"
          value={p.row.debit}
          disabled={p.readOnly}
          onChange={(e) => p.onDebit(e.currentTarget.value)}
        />
        <FormField
          label="Credit"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="0.00"
          value={p.row.credit}
          disabled={p.readOnly}
          onChange={(e) => p.onCredit(e.currentTarget.value)}
        />
      </div>
    </div>
  )
}
